import React, { createContext, useContext, useEffect, useState } from "react";

const MOSFragebogenContext = createContext(null);

export const defaultT1 = {
  alter: "", geschlecht: "", berufsfeld: "", berufsstatus: "",
  erkrankungen: [], erkrankungenSonstiges: "", gehstrecke: "", wunden: "",
  wundenLokalisierung: {}, wundenErwartung: "", krankenhausErwartung: "", aktivitaeten: {},
  arztZugehoert: "", arztBesprochen: "", erwartungenAngepasst: "",
  schuheAussehen: "", andereMeinung: "", schuhePassen: "",
  schuheGehstrecke: "", schuheVsAktuell: "", prioritaet: "",
  vorteileUeberwiegen: "", anmerkungen: "", schwierigkeit: "", zeitMinuten: "",
};

const defaultT2 = {
  techZugehoert: "", techBesprochen: "",
  techErwartungenAngepasst: "", gestaltungMitentschieden: "",
};

export const defaultPP = {
  alter: "", geschlecht: "", berufsfeld: "", berufsstatus: "",
  gehstrecke: "", gehfaehigkeit: "", gesundheitszustand: "",
  wundenVer: [], krankenhaus: "",
  aussehen: "", andereMeinung: "",
  passen: "", passenVsErwartet: "", gehen: "", gehenVsErwartet: "",
  gewicht: "", gewichtVsErwartet: "", anziehen: "", aktivitaeten: {},
  wundenAktuell: "", wundenLokalisierung: {}, trageHaeufigkeit: "", stunden: "", tragenWieErwartet: "",
  nutzungZufrieden: "", arztNachkontrolle: "", techNachkontrolle: "",
  prioritaet: "", vorteile: "", nachteile: "", vorteileUeberwiegen: "",
  zieleErfuellt: "", zielGrund: "", gebrauchstauglichkeit: "",
  weitereEigenschaften: "", anmerkungen: "", schwierigkeit: "", zeitMinuten: "",
};

const generatePatientenId = () => {
  const random = Math.floor(Math.random() * 900) + 100;
  return `${Date.now()}${random}`;
};

const makeDefaultData = () => ({
  patientenId: generatePatientenId(),
  name: "",
  vorname: "",
  step: 0,
  fragebogenId: null,
  t1: { ...defaultT1 },
  t2: { ...defaultT2 },
  pp: { ...defaultPP },
});

// ── Parameterized provider — used internally only ───────────────────────────
function MOSFragebogenProvider({ children, storageKey }) {
  const [data, setData] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      const parsed = stored ? { ...makeDefaultData(), ...JSON.parse(stored) } : makeDefaultData();
      if (!parsed.patientenId) parsed.patientenId = generatePatientenId();
      return parsed;
    } catch {
      return makeDefaultData();
    }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch {}
  }, [data, storageKey]);

  const setT1 = (field, value) =>
    setData((prev) => ({ ...prev, t1: { ...prev.t1, [field]: value } }));

  const setT2 = (field, value) =>
    setData((prev) => ({ ...prev, t2: { ...prev.t2, [field]: value } }));

  const setPP = (field, value) =>
    setData((prev) => ({ ...prev, pp: { ...prev.pp, [field]: value } }));

  const clearMOSFragebogen = () => {
    const fresh = makeDefaultData();
    setData(fresh);
    try { localStorage.removeItem(storageKey); } catch {}
  };

  return (
    <MOSFragebogenContext.Provider value={{ data, setData, setT1, setT2, setPP, clearMOSFragebogen }}>
      {children}
    </MOSFragebogenContext.Provider>
  );
}

// ── Public provider — no localStorage, memory-only for privacy ──────────────
export function MOSFragebogenPublicProvider({ children }) {
  const [data, setData] = useState(makeDefaultData);

  const setT1 = (field, value) =>
    setData((prev) => ({ ...prev, t1: { ...prev.t1, [field]: value } }));

  const setT2 = (field, value) =>
    setData((prev) => ({ ...prev, t2: { ...prev.t2, [field]: value } }));

  const setPP = (field, value) =>
    setData((prev) => ({ ...prev, pp: { ...prev.pp, [field]: value } }));

  const clearMOSFragebogen = () => setData(makeDefaultData());

  return (
    <MOSFragebogenContext.Provider value={{ data, setData, setT1, setT2, setPP, clearMOSFragebogen }}>
      {children}
    </MOSFragebogenContext.Provider>
  );
}

// ── Admin provider — for logged-in admin filling/viewing forms ───────────────
export function MOSFragebogenAdminProvider({ children }) {
  return (
    <MOSFragebogenProvider storageKey="mos_admin">{children}</MOSFragebogenProvider>
  );
}

export const useMOSFragebogen = () => {
  const ctx = useContext(MOSFragebogenContext);
  if (!ctx) throw new Error("useMOSFragebogen must be used within a MOSFragebogen provider");
  return ctx;
};
